/**
 * Pure utility functions for board ordering and filtering logic
 * These functions handle the business logic of reordering favorite boards
 * and filtering boards based on user preferences.
 */

import type { JswBoardsRefactored$data } from './__generated__/JswBoardsRefactored.graphql';

// Types for the board ordering utilities
type BoardEdge = JswBoardsRefactored$data['jira']['jiraProjectByKey']['boards'] | null;

export type SidebarOrderItem = {
	menuId: string;
	visible: boolean;
};

export type BoardData = {
	id: string;
	name: string;
	favourite: boolean;
};

/**
 * Gets the total count of favorite boards
 * Pure function - utility for UI logic
 */
export function getFavoriteBoardCount(boardEdges: BoardEdge): number {
	return (boardEdges?.edges || []).filter(
		(edge) => edge?.node?.favouriteValue?.isFavourite === true,
	).length;
}

/**
 * Generic version that works with GraphQL edge types
 * Applies custom ordering while preserving original edge types
 */
export function getOrderedFavoriteBoardsWithOriginalTypes<
	T extends {
		node?: {
			boardId?: number | string | null;
			favouriteValue?: { isFavourite?: boolean | null } | null;
			[key: string]: unknown; // Allow additional properties from GraphQL
		} | null;
	},
>(boardEdges: readonly (T | null | undefined)[], sidebarOrder: SidebarOrderItem[]): T[] {
	// Filter out null/undefined edges and ensure we have valid edges
	const validEdges = boardEdges.filter((edge): edge is T => edge != null);

	// Filter to only favorite boards
	const favoriteBoards = validEdges.filter(
		(edge) => edge?.node?.favouriteValue?.isFavourite === true,
	);

	if (sidebarOrder.length === 0) {
		// No custom order preferences, return favorites in original order
		return favoriteBoards;
	}

	// Create a map for quick board lookup
	const boardMap = new Map<string, T>();
	favoriteBoards.forEach((boardEdge) => {
		if (boardEdge?.node?.boardId) {
			boardMap.set(boardEdge.node.boardId.toString(), boardEdge);
		}
	});

	// Track which boards we've already included in the ordered list
	const usedBoardIds = new Set<string>();

	// First, get boards in the order specified by sidebar preferences
	const orderedFavoriteBoards = sidebarOrder
		.map((sidebarItem) => {
			if (!sidebarItem.menuId || !sidebarItem.visible) {
				return null;
			}

			const boardEdge = boardMap.get(sidebarItem.menuId);

			// Only include if it's actually a favorite board
			if (boardEdge?.node?.favouriteValue?.isFavourite) {
				usedBoardIds.add(sidebarItem.menuId);
				return boardEdge;
			}

			return null;
		})
		.filter((boardEdge): boardEdge is T => boardEdge !== null);

	// Then add any favorite boards not in the sidebar order (newly favorited boards)
	const remainingFavoriteBoards = favoriteBoards.filter((boardEdge) => {
		if (!boardEdge?.node?.boardId) {
			return false;
		}
		return !usedBoardIds.has(boardEdge.node.boardId.toString());
	});

	return [...orderedFavoriteBoards, ...remainingFavoriteBoards];
}

/**
 * Evaluates if there are any favorite boards available
 */
export function hasFavoriteBoards(boardEdges: BoardEdge): boolean {
	return getFavoriteBoardCount(boardEdges) > 0;
}

/**
 * Evaluates if there are more favorite boards than can be displayed
 * This is used to determine if pagination or "view more" is needed
 */
export function hasMoreFavoriteBoards(boardEdges: BoardEdge, maxDisplayCount: number): boolean {
	const favoriteCount = getFavoriteBoardCount(boardEdges);
	return favoriteCount > maxDisplayCount;
}
